import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function ShipTeslaPage() {
  // Dynamically manage SEO browser titles & meta definitions 
  useEffect(() => {
  document.title = "Ship a Tesla Across USA | Fast & Secure Tesla Transport";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Looking to ship a Tesla? Learn the complete Tesla shipping process, compare transport options, understand timelines, and track your vehicle from pickup to delivery.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'How to ship a Tesla safely, Tesla car transport process USA, Tesla shipping guide, EV shipping step-by-step guide, Tesla vehicle transport process, Tesla shipping timeline and tracking, Tesla delivery tracking, Automated vehicle shipping process, Electric vehicle transport guide, EV carrier handling guidelines, Tesla shipping checklist, Tesla transport preparation guide, Door-to-door Tesla shipping, Tesla shipping explained'
  );
}, []);

  const faqs = [
    {
      question: "Is it safe to ship a Tesla?",
      answer: "Yes. With a licensed, insured, EV-aware carrier using soft-strap tie-downs, Teslas ship safely every day. Enclosed transport adds protection from weather and road debris for extra peace of mind."
    },
    {
      question: "Is the deposit refundable?",
      answer: "Yes -- your deposit is fully refundable until dispatch is confirmed. Once a carrier is assigned, the deposit becomes non-refundable."
    },
    {
      question: "Can you ship a Tesla that won't drive?",
      answer: "Often yes, but inoperable vehicles need special equipment -- tell us at quote time so we assign the right carrier."
    }
  ];

  const costData = [
    { distance: "~500 miles", open: "~$775", enclosed: "~$1,190" },
    { distance: "~1,000 miles", open: "~$1,175", enclosed: "~$1,840" },
    { distance: "~1,500 miles", open: "~$1,575", enclosed: "~$2,490" },
    { distance: "~2,000 miles", open: "~$1,825", enclosed: "~$2,940" },
    { distance: "~2,800 miles (coast-to-coast)", open: "~$2,225", enclosed: "~$3,660" }
  ];

  // Combined Graph Schema (HowTo + Dynamic FAQPage)
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "HowTo",
        "name": "How to Ship a Tesla",
        "step": [
          { "@type": "HowToStep", "text": "Request a shipping quote" },
          { "@type": "HowToStep", "text": "Choose open or enclosed transport" },
          { "@type": "HowToStep", "text": "Schedule pickup" },
          { "@type": "HowToStep", "text": "Vehicle delivery" }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Injecting Structured SEO Data into the DOM */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Guides &amp; Options</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground" data-testid="guide-title">
              How to Ship a Tesla: Costs, Options, and What to Expect
            </h1>
          </div>

          {/* Hook Callout */}
          <div className="glass border-l-4 border-l-accent border-y border-r border-border rounded-r-xl p-6 mb-8 text-lg text-foreground">
            Shipping a Tesla is simpler than most people expect. You can opt for open transport for a more budget-friendly option, or choose enclosed transport for extra protection for your vehicle. Just request a quick quote, and a trusted carrier experienced with EVs will handle everything from pickup to delivery right at your doorstep. Pricing typically starts at around $775 for shorter open shipments and can exceed $3,700 for long-distance enclosed transport, depending on the distance and the level of protection you choose. 
          </div>

          <div className="text-center mb-12">
            <Link to="/quote">
              <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold shadow-lg shadow-accent/20 hover:brightness-110 transition-all">
                Get Instant Quote
              </Button>
            </Link>
          </div>

          {/* Body Prose */}
          <div className="prose dark:prose-invert max-w-none space-y-6 text-muted-foreground leading-relaxed">
            <p>
              Whether you bought a Model 3 across the country, are relocating with your Model Y, or need a Plaid moved to a new garage without adding miles, ShipTesla handles it with carriers who understand electric vehicles. Below is exactly how it works, what it costs, and how to prepare your car.
            </p>

            {/* Section 1 */}
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground pt-6">
              Open vs.Enclosed transport for a Tesla
            </h2>
            <p>
              <strong className="text-foreground">Open transport</strong> moves your Tesla on a standard multi-car carrier. It's the most widely used and budget-friendly option, with more carrier availability and quicker pickup windows. Your vehicle remains exposed to weather and road conditions--just like normal highway driving--which works perfectly well for most shipments.

            </p>
            <p>
              <strong className="text-foreground">Enclosed transport</strong> ships your Tesla in a fully covered trailer, offering complete protection from weather, road debris, and visibility. It's the preferred option for new deliveries, Performance and Plaid models, wrapped or custom-painted vehicles, and any high-value Tesla you want fully protected from pickup to delivery. Explore our enclosed Tesla transport guide for a detailed comparison. See our <Link to="/enclosed-tesla-transport" className="text-accent hover:underline">enclosed Tesla transport guide</Link> for a full comparison.
            </p>

            {/* Section 2 & Table */}
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground pt-6">
              How much does it cost to ship a Tesla?
            </h2>
            <p>
              ShipTesla pricing is transparent and mileage-based: open transport starts at <strong className="text-foreground">$1.05 per mile plus a $250 base</strong>; enclosed starts at <strong className="text-foreground">$1.68 per mile plus a $350 base</strong>. Per-mile rates step down with distance -- as low as $0.50/mile on long routes. Short-distance minimums apply. A few representative examples:
            </p>

            <div className="overflow-x-auto my-6 rounded-xl border border-border glass">
              <table className="w-full text-left border-collapse font-sans text-sm md:text-base">
                <thead>
                  <tr className="bg-muted/50 text-foreground border-b border-border">
                    <th className="p-4 font-semibold">Distance</th>
                    <th className="p-4 font-semibold">Open</th>
                    <th className="p-4 font-semibold">Enclosed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {costData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-muted/20 transition-colors">
                      <td className="p-4 font-medium text-foreground">{row.distance}</td>
                      <td className="p-4 text-muted-foreground">{row.open}</td>
                      <td className="p-4 text-muted-foreground">{row.enclosed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p>
              These are illustrative; your exact price depends on route, season, and vehicle condition. See the full <Link to="/tesla-shipping-cost" className="text-accent hover:underline">Tesla shipping cost guide</Link> or get an instant quote in under 60 seconds for your specific move.
            </p>

            <div className="text-center py-4">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold shadow-lg shadow-accent/20 hover:brightness-110 transition-all">
                  Get Instant Quote
                </Button>
              </Link>
            </div>

            {/* Section 3 */}
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground pt-6">
              How it works -- three simple steps
            </h2>
            <ol className="list-decimal pl-6 space-y-3 text-muted-foreground">
              <li><strong className="text-foreground">Get an instant quote in minutes.</strong> Enter your Tesla details, pickup and delivery locations, and choose between open or enclosed transport to see real-time pricing.</li>
              <li><strong className="text-foreground">Book with a risk-free deposit. </strong> Secure your shipment with a fully refundable deposit--cancel anytime before a carrier is officially assigned and dispatched.
</li>
              <li><strong className="text-foreground">Track every step, door to door.</strong> Stay informed with real-time SMS and email updates from carrier assignment through final delivery--complete transparency, no guesswork.
</li>
            </ol>

            {/* Section 4 */}
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground pt-6">
              Why ship with a Tesla specialist
            </h2>
            <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
              <li><strong className="text-foreground">Verified and fully insured carriers.</strong> Every carrier is licensed, insured, and vetted to our standards before they ever touch your car.</li>
              <li><strong className="text-foreground">EV-specific expertise.</strong>From soft-strap securing to low-clearance loading and proper battery charge guidance, your Tesla is handled with electric-vehicle precision.
</li>
              <li><strong className="text-foreground">Clear, honest pricing.</strong> Our margin-protected dispatch model ensures fair rates for you and dependable service from carriers--no bait-and-switch pricing tactics.</li>
              <li><strong className="text-foreground">Real-time communication.</strong> Get proactive updates at every stage, from booking to delivery, via SMS and email--so you're always in the loop.</li>
            </ul>

            {/* Section 5 */}
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground pt-6">
              How to prepare your Tesla for transport
            </h2>
            <p>
               Set your battery to approximately 50%--sufficient for seamless loading and unloading without the drawbacks of a full or critically low charge. Remove all personal items and loose accessories, fold in the mirrors, and be prepared to brief the carrier on features such as Sentry Mode and PIN-to-Drive. At pickup, document any pre-existing cosmetic imperfections, as the driver will complete a detailed condition report.

            </p>

            {/* Section 6 */}
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground pt-6">
              How long does it take?
            </h2>
            <p>
              Transit times for Tesla shipments typically range from 1 to 9 days, depending on distance, in addition to a pickup window (we recommend allowing at least 72 hours). Cross-country shipments generally average 7--9 days door to door. All timelines are estimates rather than guarantees, as carriers may adjust routes in response to weather conditions and traffic patterns.
            </p>
          </div>

          {/* Section 7 - Accordion FAQs */}
          <div className="mt-16">
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
                >
                  <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Bottom CTA Section */}
          <div className="mt-16 text-center border-t border-border pt-12">
            <p className="text-muted-foreground mb-6">
              Still have questions? We're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 border-border text-foreground hover:bg-muted/30">
                  Contact Us
                </Button>
              </Link>
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold">
                  Get Your Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Section 8 - Related Guides Footer Area */}
          <div className="mt-16 border-t border-border pt-8">
            <h3 className="font-outfit text-lg font-semibold text-foreground mb-4">Related guides</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/tesla-shipping-cost" className="text-accent hover:underline">
                  Tesla Shipping Cost: 2026 Price Guide
                </Link>
              </li>
              <li>
                <Link to="/enclosed-tesla-transport" className="text-accent hover:underline">
                  Enclosed Tesla Transport
                </Link>
              </li>
              <li>
                <Link to="/routes/california-to-texas" className="text-accent hover:underline">
                  Ship a Tesla: California to Texas
                </Link>
              </li>
            </ul>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}






